import React from 'react';
import type { ClassLessonAlarmOffset, ClassLessonAlarmSoundKind } from '../../utils/classLessonAlarmSettings';
import {
  CLASS_LESSON_ALARM_OFFSET_OPTIONS,
  CLASS_LESSON_ALARM_SOUND_OPTIONS,
  classLessonAlarmOffsetLabel,
  classLessonAlarmSoundLabel,
} from '../../utils/classLessonAlarmSettings';

export interface ClassLessonAlarmPreviewBlockItem {
  startTime: string;
  channel: 'regular' | 'dedicated';
  channelLabel: string;
  offset: ClassLessonAlarmOffset;
  offsetLabel: string;
  soundKind: ClassLessonAlarmSoundKind;
  soundLabel: string;
  gradeLabel: string;
  periodLabel: string;
  /** 연간시간표에 오늘 실제 수업 편성 (자동 모드) / 커스텀에서는 항상 true */
  hasLesson: boolean;
  /** 실제 울림 예정 */
  willRing: boolean;
  gradeId?: string;
  slotIndex?: number;
}

function formatDisplayTime(hhmm: string): { period: string | null; clock: string } {
  const [hRaw, mRaw] = hhmm.split(':').map(Number);
  if (!Number.isFinite(hRaw) || !Number.isFinite(mRaw)) {
    return { period: null, clock: hhmm };
  }
  const period = hRaw < 12 ? '오전' : '오후';
  const h12 = hRaw % 12 || 12;
  return {
    period,
    clock: `${String(h12).padStart(2, '0')}:${String(mRaw).padStart(2, '0')}`,
  };
}

function parseHHMM(hhmm: string): { hour24: number; minute: number } | null {
  const [hRaw, mRaw] = hhmm.split(':').map(Number);
  if (!Number.isFinite(hRaw) || !Number.isFinite(mRaw)) return null;
  if (hRaw < 0 || hRaw > 23 || mRaw < 0 || mRaw > 59) return null;
  return { hour24: hRaw, minute: mRaw };
}

function toHHMM(hour24: number, minute: number): string {
  return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function hour24ToParts(hour24: number): { isPm: boolean; hour12: number } {
  const isPm = hour24 >= 12;
  const hour12 = hour24 % 12 || 12;
  return { isPm, hour12 };
}

function partsToHour24(isPm: boolean, hour12: number): number {
  const h = Math.min(12, Math.max(1, hour12));
  if (!isPm) return h === 12 ? 0 : h;
  return h === 12 ? 12 : h + 12;
}

function clampInt(raw: string, min: number, max: number): number | null {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  const n = Number(digits);
  if (!Number.isFinite(n)) return null;
  return Math.min(max, Math.max(min, n));
}

const DEFAULT_EMPTY_HOUR12 = 12;
const DEFAULT_EMPTY_MINUTE = 0;

/** 시·분 인라인 편집 — 백스페이스 시 전체 숫자 삭제, 비우면 12:00 */
const ClassLessonAlarmClockEditor: React.FC<{
  startTime: string;
  disabled?: boolean;
  labelPrefix: string;
  onChangeTime?: (time: string) => void;
  onInteract?: (e: React.SyntheticEvent) => void;
}> = ({ startTime, disabled = false, labelPrefix, onChangeTime, onInteract }) => {
  const parsed = parseHHMM(startTime);
  const parts = parsed ? hour24ToParts(parsed.hour24) : { isPm: false, hour12: DEFAULT_EMPTY_HOUR12 };
  const committedMinute = parsed?.minute ?? DEFAULT_EMPTY_MINUTE;

  const [hourText, setHourText] = React.useState(() => String(parts.hour12).padStart(2, '0'));
  const [minuteText, setMinuteText] = React.useState(() =>
    String(committedMinute).padStart(2, '0')
  );
  const [isPm, setIsPm] = React.useState(parts.isPm);
  const focusedRef = React.useRef(false);

  React.useEffect(() => {
    if (focusedRef.current) return;
    const next = parseHHMM(startTime);
    if (!next) {
      setHourText(String(DEFAULT_EMPTY_HOUR12).padStart(2, '0'));
      setMinuteText(String(DEFAULT_EMPTY_MINUTE).padStart(2, '0'));
      setIsPm(false);
      return;
    }
    const p = hour24ToParts(next.hour24);
    setHourText(String(p.hour12).padStart(2, '0'));
    setMinuteText(String(next.minute).padStart(2, '0'));
    setIsPm(p.isPm);
  }, [startTime]);

  const commit = (nextIsPm: boolean, hourRaw: string, minuteRaw: string) => {
    const hour12 = clampInt(hourRaw, 1, 12) ?? DEFAULT_EMPTY_HOUR12;
    const minute = clampInt(minuteRaw, 0, 59) ?? DEFAULT_EMPTY_MINUTE;
    setHourText(String(hour12).padStart(2, '0'));
    setMinuteText(String(minute).padStart(2, '0'));
    setIsPm(nextIsPm);
    onChangeTime?.(toHHMM(partsToHour24(nextIsPm, hour12), minute));
  };

  const clearAllDigits = () => {
    setHourText('');
    setMinuteText('');
  };

  const handleDigitKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault();
      clearAllDigits();
    }
  };

  const handleBlur = () => {
    focusedRef.current = false;
    const hourEmpty = !hourText.replace(/\D/g, '');
    const minuteEmpty = !minuteText.replace(/\D/g, '');
    if (hourEmpty && minuteEmpty) {
      commit(isPm, String(DEFAULT_EMPTY_HOUR12), String(DEFAULT_EMPTY_MINUTE));
      return;
    }
    commit(isPm, hourText || String(DEFAULT_EMPTY_HOUR12), minuteText || String(DEFAULT_EMPTY_MINUTE));
  };

  return (
    <>
      <button
        type="button"
        className="cp-class-lesson-alarm-edu-ampm is-edit"
        disabled={disabled}
        aria-label="오전/오후 전환"
        onClick={(e) => {
          onInteract?.(e);
          const hour12 = clampInt(hourText, 1, 12) ?? DEFAULT_EMPTY_HOUR12;
          const minute = clampInt(minuteText, 0, 59) ?? DEFAULT_EMPTY_MINUTE;
          commit(!isPm, String(hour12), String(minute));
        }}
      >
        {isPm ? '오후' : '오전'}
      </button>
      <span className="cp-class-lesson-alarm-edu-clock is-edit">
        <input
          type="text"
          inputMode="numeric"
          className="cp-class-lesson-alarm-edu-clock-digit"
          value={hourText}
          placeholder="12"
          disabled={disabled}
          aria-label={`${labelPrefix} 시`}
          maxLength={2}
          onClick={onInteract}
          onFocus={(e) => {
            focusedRef.current = true;
            e.currentTarget.select();
          }}
          onBlur={handleBlur}
          onKeyDown={handleDigitKeyDown}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
            setHourText(digits);
            if (digits.length === 2) {
              const hour12 = clampInt(digits, 1, 12) ?? DEFAULT_EMPTY_HOUR12;
              const minute = clampInt(minuteText, 0, 59) ?? DEFAULT_EMPTY_MINUTE;
              commit(isPm, String(hour12), String(minute));
            }
          }}
        />
        <span className="cp-class-lesson-alarm-edu-clock-sep">:</span>
        <input
          type="text"
          inputMode="numeric"
          className="cp-class-lesson-alarm-edu-clock-digit"
          value={minuteText}
          placeholder="00"
          disabled={disabled}
          aria-label={`${labelPrefix} 분`}
          maxLength={2}
          onClick={onInteract}
          onFocus={(e) => {
            focusedRef.current = true;
            e.currentTarget.select();
          }}
          onBlur={handleBlur}
          onKeyDown={handleDigitKeyDown}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
            setMinuteText(digits);
            if (digits.length === 2) {
              const hour12 = clampInt(hourText, 1, 12) ?? DEFAULT_EMPTY_HOUR12;
              const minute = clampInt(digits, 0, 59) ?? DEFAULT_EMPTY_MINUTE;
              commit(isPm, String(hour12), String(minute));
            }
          }}
        />
      </span>
    </>
  );
};

/** 에듀코디 AlarmBlock 축소 형태 — 설정 모달 미리보기/커스텀 편집용 */
const ClassLessonAlarmPreviewBlock: React.FC<{
  item: ClassLessonAlarmPreviewBlockItem;
  editable?: boolean;
  disabled?: boolean;
  onChangeTime?: (time: string) => void;
  onChangeOffset?: (offset: ClassLessonAlarmOffset) => void;
  onChangeChannel?: (channel: 'regular' | 'dedicated') => void;
  onChangeSoundKind?: (soundKind: ClassLessonAlarmSoundKind) => void;
  onToggleEnabled?: () => void;
}> = ({
  item,
  editable = false,
  disabled = false,
  onChangeTime,
  onChangeOffset,
  onChangeChannel,
  onChangeSoundKind,
  onToggleEnabled,
}) => {
  const { period, clock } = formatDisplayTime(item.startTime);
  const channelLabel = item.channel === 'dedicated' ? '전담' : '일반';
  const stateClass = editable
    ? item.willRing
      ? 'is-active'
      : 'is-inactive'
    : item.hasLesson
      ? item.willRing
        ? 'is-active'
        : 'is-active is-channel-off'
      : 'is-inactive';

  const stop = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  return (
    <article
      className={`cp-class-lesson-alarm-edu-block is-${item.channel} ${stateClass}${
        editable ? ' is-editable' : ''
      }${editable && !disabled ? ' is-clickable' : ''}`}
      aria-label={`${item.gradeLabel} ${item.periodLabel} ${item.startTime} ${item.offsetLabel}${
        editable
          ? item.willRing
            ? ' 활성'
            : ' 비활성'
          : item.hasLesson
            ? item.willRing
              ? ' 활성'
              : ' 채널 꺼짐'
            : ' 미편성'
      }`}
      onClick={
        editable && !disabled && onToggleEnabled
          ? () => {
              onToggleEnabled();
            }
          : undefined
      }
      onKeyDown={
        editable && !disabled && onToggleEnabled
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggleEnabled();
              }
            }
          : undefined
      }
      role={editable ? 'button' : undefined}
      tabIndex={editable && !disabled ? 0 : undefined}
    >
      <div className="cp-class-lesson-alarm-edu-block-row">
        {editable ? (
          <button
            type="button"
            className={`cp-class-lesson-alarm-edu-badge is-channel is-${item.channel} is-toggle`}
            disabled={disabled}
            aria-label={`채널 ${channelLabel} — 클릭하여 전환`}
            onClick={(e) => {
              stop(e);
              onChangeChannel?.(item.channel === 'dedicated' ? 'regular' : 'dedicated');
            }}
          >
            {channelLabel}
          </button>
        ) : (
          <span className={`cp-class-lesson-alarm-edu-badge is-channel is-${item.channel}`}>
            {item.hasLesson ? item.channelLabel : '미편성'}
          </span>
        )}
        <div className="cp-class-lesson-alarm-edu-time">
          {editable ? (
            <ClassLessonAlarmClockEditor
              startTime={item.startTime}
              disabled={disabled}
              labelPrefix={`${item.gradeLabel} ${item.periodLabel}`}
              onChangeTime={onChangeTime}
              onInteract={stop}
            />
          ) : (
            <>
              {period ? <span className="cp-class-lesson-alarm-edu-ampm">{period}</span> : null}
              <span className="cp-class-lesson-alarm-edu-clock">{clock}</span>
            </>
          )}
        </div>
        {editable ? (
          <select
            className="cp-class-lesson-alarm-edu-offset-select"
            value={item.offset}
            disabled={disabled}
            aria-label={`${item.gradeLabel} ${item.periodLabel} 알람 오프셋`}
            onClick={stop}
            onChange={(e) => onChangeOffset?.(e.target.value as ClassLessonAlarmOffset)}
          >
            {CLASS_LESSON_ALARM_OFFSET_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <span className="cp-class-lesson-alarm-edu-badge is-offset">
            {classLessonAlarmOffsetLabel(item.offset)}
          </span>
        )}
      </div>
      <div className="cp-class-lesson-alarm-edu-name">
        <span className="cp-class-lesson-alarm-edu-name-text">
          {item.gradeLabel} {item.periodLabel}
        </span>
        <span className="cp-class-lesson-alarm-edu-sound" aria-hidden>
          ·
        </span>
        {editable ? (
          <select
            className="cp-class-lesson-alarm-edu-sound-select"
            value={item.soundKind}
            disabled={disabled}
            aria-label={`${item.gradeLabel} ${item.periodLabel} 알람 종류`}
            onClick={stop}
            onChange={(e) => onChangeSoundKind?.(e.target.value as ClassLessonAlarmSoundKind)}
          >
            {CLASS_LESSON_ALARM_SOUND_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <span className="cp-class-lesson-alarm-edu-sound">
            {item.hasLesson ? classLessonAlarmSoundLabel(item.soundKind) : '수업 없음'}
          </span>
        )}
      </div>
    </article>
  );
};

export default ClassLessonAlarmPreviewBlock;
